import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  User, Mail, MapPin, Calendar, Receipt, Phone, MessageSquare, FileText, Trash2, Plus, Loader2, ChevronDown, ChevronUp, ChevronRight, Activity, ShieldAlert, Gift, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import API from "@/lib/api";
import ClickToCallPhone from "@/components/ClickToCallPhone";
import RevealPhi from "@/components/RevealPhi";
import ClickToTextPhone from "@/components/ClickToTextPhone";
import PatientWellnessPanel from "@/components/PatientWellnessPanel";
import IdVerificationReviewPanel from "@/components/IdVerificationReviewPanel";
import PatientRewardsTab from "@/components/PatientRewardsTab";
import PatientPaymentTimeline from "@/components/PatientPaymentTimeline";
import { BreakGlassButton } from "@/components/BreakGlassControls";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";

/**
 * Unified patient detail modal — opens when staff click a patient name from
 * any order queue / list. One-stop view to call/text the patient and read or
 * append patient-level notes.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - patientId: string (required when open)
 *   - initialTab?: string — which tab to open first (default "wellness").
 *     Pass "id-verify" from the Order Queue ID-review badge so the clinician
 *     lands directly on the identity docs.
 */
export default function PatientDetailModal({ open, onClose, patientId, initialTab = "wellness" }) {
  const [profile, setProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [projectedOrders, setProjectedOrders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [calls, setCalls] = useState([]);
  const [texts, setTexts] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pendingPlanChange, setPendingPlanChange] = useState(null);
  const [planChangeForm, setPlanChangeForm] = useState({ subscription_id: "", target_tier: "" });
  const [planChangeBusy, setPlanChangeBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState("");
  const [newNoteCat, setNewNoteCat] = useState("general");
  const [savingNote, setSavingNote] = useState(false);
  const [expandedHistorySid, setExpandedHistorySid] = useState(null);
  const [historyBySid, setHistoryBySid] = useState({});
  const [loadingHistorySid, setLoadingHistorySid] = useState(null);

  const submitStaffTierChange = async () => {
    const { subscription_id, target_tier } = planChangeForm;
    if (!subscription_id || !target_tier) {
      toast.error("Pick a subscription and a target tier");
      return;
    }
    setPlanChangeBusy(true);
    try {
      const { data } = await API.post(
        `/subscriptions/${subscription_id}/staff-tier-change`,
        { user_id: patientId, target_tier },
      );
      toast.success(`Switching to ${data.to_tier} on ${data.effective_at?.slice(0, 10) || "next renewal"}`);
      // Refresh
      const subs = await API.get(`/subscriptions/admin/patient/${encodeURIComponent(patientId)}`).catch(() => ({ data: { subscriptions: [], pending_plan_change: null } }));
      setSubscriptions(subs?.data?.subscriptions || []);
      setPendingPlanChange(subs?.data?.pending_plan_change || null);
      setPlanChangeForm({ subscription_id: "", target_tier: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plan change failed");
    } finally {
      setPlanChangeBusy(false);
    }
  };

  const toggleHistory = useCallback(async (sid) => {
    if (!sid) return;
    if (expandedHistorySid === sid) {
      setExpandedHistorySid(null);
      return;
    }
    setExpandedHistorySid(sid);
    if (historyBySid[sid]) return; // cached
    setLoadingHistorySid(sid);
    try {
      const { data } = await API.get(`/receipts/${encodeURIComponent(sid)}/email-history`);
      setHistoryBySid(prev => ({ ...prev, [sid]: Array.isArray(data) ? data : [] }));
    } catch {
      toast.error("Couldn't load receipt send history.");
      setHistoryBySid(prev => ({ ...prev, [sid]: [] }));
    } finally {
      setLoadingHistorySid(null);
    }
  }, [expandedHistorySid, historyBySid]);

  const reloadNotes = useCallback(() => {
    if (!patientId) return;
    API.get(`/patients/${encodeURIComponent(patientId)}/notes`)
      .then(({ data }) => setNotes(Array.isArray(data) ? data : []))
      .catch(() => setNotes([]));
  }, [patientId]);

  useEffect(() => {
    if (!open || !patientId) return;
    let alive = true;
    setLoading(true);
    setProfile(null); setOrders([]); setNotes([]); setCalls([]); setTexts([]); setReceipts([]); setProjectedOrders([]); setSubscriptions([]); setPendingPlanChange(null);

    Promise.all([
      // Try the prescriber endpoint first (returns demographics + most fields)
      API.get(`/prescriber/patients/${encodeURIComponent(patientId)}`).catch(() => ({ data: null })),
      API.get(`/patients/${encodeURIComponent(patientId)}/notes`).catch(() => ({ data: [] })),
      API.get(`/calls/history/${encodeURIComponent(patientId)}`).catch(() => ({ data: [] })),
      API.get(`/messages/history/${encodeURIComponent(patientId)}`).catch(() => ({ data: [] })),
      API.get(`/receipts/by-user/${encodeURIComponent(patientId)}`).catch(() => ({ data: [] })),
      API.get(`/admin/patients/${encodeURIComponent(patientId)}/projected-orders`).catch(() => ({ data: [] })),
      API.get(`/subscriptions/admin/patient/${encodeURIComponent(patientId)}`).catch(() => ({ data: { subscriptions: [], pending_plan_change: null } })),
    ]).then(([p, n, c, t, r, pj, subs]) => {
      if (!alive) return;
      const pp = p?.data?.patient || p?.data || null;
      setProfile(pp);
      setOrders(p?.data?.orders || []);
      setNotes(Array.isArray(n.data) ? n.data : []);
      setCalls(Array.isArray(c.data) ? c.data : []);
      setTexts(Array.isArray(t.data) ? t.data : []);
      setReceipts(Array.isArray(r.data) ? r.data : []);
      setProjectedOrders(Array.isArray(pj.data) ? pj.data : []);
      setSubscriptions(subs?.data?.subscriptions || []);
      setPendingPlanChange(subs?.data?.pending_plan_change || null);
    }).finally(() => alive && setLoading(false));

    return () => { alive = false; };
  }, [open, patientId]);

  const addNote = async () => {
    if (!newNote.trim()) { toast.info("Type a note first."); return; }
    setSavingNote(true);
    try {
      await API.post(`/patients/${encodeURIComponent(patientId)}/notes`, { body: newNote.trim(), category: newNoteCat });
      toast.success("Note added");
      setNewNote("");
      reloadNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save note");
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!(await brandedConfirm({ title: 'Delete?', description: "Delete this note?", confirmText: 'Delete', cancelText: 'Cancel', tone: 'danger' }))) return;
    try {
      await API.delete(`/patients/${encodeURIComponent(patientId)}/notes/${encodeURIComponent(noteId)}`);
      toast.success("Note deleted");
      reloadNotes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
    catch { return iso; }
  };
  const fmtMoney = (a, ccy) => {
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: (ccy || "USD") }).format(a); }
    catch { return `$${Number(a || 0).toFixed(2)}`; }
  };

  const phone = profile?.phone || "";
  const name = profile?.name || profile?.email || "Patient";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="patient-detail-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }} className="flex items-center gap-2">
            <User className="w-5 h-5 text-purple-600" />
            <span>{name}</span>
            {profile?.patient_display_id && (
              <span
                className="ml-2 text-xs font-mono font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200"
                title="Patient display ID — quote to support and pharmacies"
                data-testid="patient-detail-display-id"
              >
                {profile.patient_display_id}
              </span>
            )}
            <div className="ml-auto">
              <BreakGlassButton patientId={patientId} />
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading && !profile ? (
          <div className="bg-slate-50 border border-slate-100 rounded-xl h-32 animate-pulse" />
        ) : !profile ? (
          <p className="text-sm text-slate-500">Couldn&apos;t load patient.</p>
        ) : (
          <>
            {/* Quick contact strip */}
            <div className="bg-purple-50 border border-purple-100 rounded-xl p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><span>{profile.email || "—"}</span></div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Phone className="w-4 h-4 text-slate-400" />
                  {phone ? (
                    <>
                      <ClickToCallPhone phone={phone} patientId={patientId} patientName={name} />
                      <ClickToTextPhone phone={phone} patientId={patientId} patientName={name} className="ml-2" />
                    </>
                  ) : <span className="text-slate-500">No phone on file</span>}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {profile.date_of_birth ? (
                    <RevealPhi
                      patientId={patientId}
                      field="dob"
                      maskedValue={`DOB ${String(profile.date_of_birth).slice(0, 4).replace(/\d/g, "•")}-••-••`}
                    />
                  ) : <span>DOB —</span>}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {profile.address ? (
                    <RevealPhi
                      patientId={patientId}
                      field="full_address"
                      maskedValue={`${String(profile.address).split(",")[0]?.slice(0, 6) || "•"}••••`}
                      className="truncate"
                    />
                  ) : <span className="truncate">No address on file</span>}
                </div>
              </div>
              {profile.duplicate_address_flag && (
                <DuplicateAddressBadge
                  profile={profile}
                  patientId={patientId}
                  onCleared={() => setProfile((p) => p ? { ...p, duplicate_address_flag: false, duplicate_address_matches: [] } : p)}
                />
              )}
            </div>

            {/* Tabs */}
            <Tabs defaultValue={initialTab} key={`${patientId}-${initialTab}`}>
              <TabsList className="grid grid-cols-8 w-full">
                <TabsTrigger value="wellness" data-testid="patient-tab-wellness"><Activity className="w-3.5 h-3.5 mr-1" />Wellness</TabsTrigger>
                <TabsTrigger value="id-verify" data-testid="patient-tab-id-verify"><ShieldAlert className="w-3.5 h-3.5 mr-1" />ID</TabsTrigger>
                <TabsTrigger value="notes" data-testid="patient-tab-notes"><FileText className="w-3.5 h-3.5 mr-1" />Notes ({notes.length})</TabsTrigger>
                <TabsTrigger value="orders" data-testid="patient-tab-orders">Orders ({orders.length})</TabsTrigger>
                <TabsTrigger value="calls" data-testid="patient-tab-calls"><Phone className="w-3.5 h-3.5 mr-1" />Calls ({calls.length})</TabsTrigger>
                <TabsTrigger value="texts" data-testid="patient-tab-texts"><MessageSquare className="w-3.5 h-3.5 mr-1" />Texts ({texts.length})</TabsTrigger>
                <TabsTrigger value="receipts" data-testid="patient-tab-receipts"><Receipt className="w-3.5 h-3.5 mr-1" />Receipts ({receipts.length})</TabsTrigger>
                <TabsTrigger value="billing" data-testid="patient-tab-billing"><CreditCard className="w-3.5 h-3.5 mr-1" />Billing</TabsTrigger>
                <TabsTrigger value="rewards" data-testid="patient-tab-rewards"><Gift className="w-3.5 h-3.5 mr-1" />Rewards</TabsTrigger>
              </TabsList>

              {/* Wellness — 12-week chart, streak, side-effect history */}
              <TabsContent value="wellness" className="mt-4">
                <PatientWellnessPanel patientId={patientId} />
              </TabsContent>

              {/* ID Verification — uploaded ID card + video attestation +
                  staff Verify / Reject controls. Phase-1 in-house flow
                  (Persona / 3rd-party scheduled for Phase 2). */}
              <TabsContent value="id-verify" className="mt-4">
                <IdVerificationReviewPanel patientId={patientId} />
              </TabsContent>

              {/* Notes */}
              <TabsContent value="notes" className="space-y-3 mt-4">
                <div className="bg-white border border-slate-200 rounded-xl p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Label className="text-xs text-slate-500">Category</Label>
                    <select
                      value={newNoteCat}
                      onChange={(e) => setNewNoteCat(e.target.value)}
                      className="text-xs border rounded-lg px-2 py-1"
                      data-testid="patient-note-category"
                    >
                      <option value="general">General</option>
                      <option value="clinical">Clinical</option>
                      <option value="billing">Billing</option>
                      <option value="shipping">Shipping</option>
                      <option value="customer-service">Customer service</option>
                    </select>
                  </div>
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note about this patient (visible to all staff)…"
                    rows={3}
                    className="rounded-xl"
                    maxLength={5000}
                    data-testid="patient-note-input"
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      size="sm"
                      onClick={addNote}
                      disabled={savingNote || !newNote.trim()}
                      className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                      data-testid="patient-note-save"
                    >
                      {savingNote ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                      Add Note
                    </Button>
                  </div>
                </div>
                {notes.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No notes yet — be the first to add one.</p>
                ) : (
                  <ul className="space-y-2" data-testid="patient-notes-list">
                    {notes.map((n) => (
                      <li key={n.id} className="bg-white border border-slate-200 rounded-xl p-3 flex items-start gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-semibold text-slate-900">{n.staff_name || "Staff"}</span>
                            <span className="text-[10px] uppercase tracking-wider font-bold text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-2 py-0.5">{n.category || "general"}</span>
                            <span className="text-[11px] text-slate-400">{fmtDate(n.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.body}</p>
                        </div>
                        <button type="button" onClick={() => deleteNote(n.id)} className="text-slate-400 hover:text-rose-500" data-testid={`patient-note-delete-${n.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              {/* Orders */}
              <TabsContent value="orders" className="mt-4">
                {/* Subscription & tier override (staff side) */}
                {(subscriptions.length > 0 || pendingPlanChange) && (
                  <div className="mb-4 rounded-xl border border-purple-200 bg-purple-50 dark:bg-purple-950/30 p-3" data-testid="staff-plan-change-section">
                    <p className="text-xs font-bold uppercase tracking-wider text-purple-700 dark:text-purple-300 mb-2">
                      Subscription & plan tier · staff override
                    </p>
                    {pendingPlanChange && (
                      <p className="text-xs text-purple-800 dark:text-purple-200 mb-2" data-testid="staff-plan-change-pending">
                        Pending change: <b>{pendingPlanChange.from_tier}</b> → <b>{pendingPlanChange.to_tier}</b> effective {pendingPlanChange.effective_at?.slice(0, 10) || "next renewal"} (requested by {pendingPlanChange.requested_by_role}).
                      </p>
                    )}
                    {subscriptions.length > 0 ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          className="text-xs rounded-lg border border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-800 px-2 py-1"
                          value={planChangeForm.subscription_id}
                          onChange={(e) => setPlanChangeForm((f) => ({ ...f, subscription_id: e.target.value }))}
                          data-testid="staff-plan-change-sub-select"
                        >
                          <option value="">Subscription…</option>
                          {subscriptions.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.product_name} · {s.current_tier || "subscription"} · ${Number(s.amount).toFixed(2)}
                            </option>
                          ))}
                        </select>
                        <select
                          className="text-xs rounded-lg border border-purple-200 dark:border-purple-800 bg-white dark:bg-slate-800 px-2 py-1"
                          value={planChangeForm.target_tier}
                          onChange={(e) => setPlanChangeForm((f) => ({ ...f, target_tier: e.target.value }))}
                          data-testid="staff-plan-change-target-select"
                        >
                          <option value="">Target tier…</option>
                          <option value="one_time">Monthly</option>
                          <option value="subscription">Bundle Subscribe</option>
                          <option value="royal_90">Royal Tier</option>
                        </select>
                        <Button
                          size="sm"
                          onClick={submitStaffTierChange}
                          disabled={planChangeBusy || !planChangeForm.subscription_id || !planChangeForm.target_tier}
                          className="rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid="staff-plan-change-submit"
                        >
                          {planChangeBusy ? "…" : "Apply override"}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500 italic">No Stripe subscriptions on file — pure one-time purchases.</p>
                    )}
                  </div>
                )}
                {projectedOrders.length > 0 && (
                  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3" data-testid="projected-orders-section">
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-2">
                      Projected coming months ({projectedOrders.length}) · staff only
                    </p>
                    <ul className="divide-y divide-amber-200/70 dark:divide-amber-900/40">
                      {projectedOrders.map((o) => (
                        <li key={o.id} className="py-1.5 text-xs flex items-center justify-between gap-3" data-testid={`projected-order-${o.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-amber-900 dark:text-amber-100 truncate">
                              Month {o.series_position}/{o.total_in_series} · {o.medication_name} {o.series_dose}
                            </p>
                            <p className="text-amber-700 dark:text-amber-300/80">
                              Auto-releases on next payment · ~{o.auto_release_after?.slice(0, 10) || "—"}
                            </p>
                          </div>
                          <span className="font-mono text-amber-600/80 dark:text-amber-400/80 shrink-0">{o.id?.slice(-8)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {orders.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No orders on file.</p>
                ) : (
                  <OrdersGroupedList orders={orders} fmtDate={fmtDate} />
                )}
              </TabsContent>

              {/* Calls */}
              <TabsContent value="calls" className="mt-4">
                {calls.length === 0 ? <p className="text-xs text-slate-400 italic">No calls logged yet.</p> : (
                  <ul className="divide-y divide-slate-100">
                    {calls.map((c) => (
                      <li key={c.id} className="py-2 text-sm">
                        <p className="font-medium">{c.staff_name || "Staff"} <span className="text-slate-400 font-normal">·</span> <span className="font-mono text-xs">{c.dialed_number}</span></p>
                        <p className="text-xs text-slate-400">{c.staff_role} · {fmtDate(c.created_at)}{c.disposition ? ` · ${c.disposition}` : ""}</p>
                        {c.note && <p className="text-xs text-slate-600 mt-1 italic">&quot;{c.note}&quot;</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              {/* Texts */}
              <TabsContent value="texts" className="mt-4">
                {texts.length === 0 ? <p className="text-xs text-slate-400 italic">No texts logged yet.</p> : (
                  <ul className="divide-y divide-slate-100">
                    {texts.map((t) => (
                      <li key={t.id} className="py-2 text-sm">
                        <p className="font-medium">{t.staff_name || "Staff"} <span className="text-slate-400 font-normal">·</span> <span className="font-mono text-xs">{t.dialed_number}</span></p>
                        <p className="text-xs text-slate-400">{t.staff_role} · {fmtDate(t.created_at)}</p>
                        {t.body && <p className="text-xs text-slate-600 mt-1 bg-slate-50 border border-slate-100 rounded-lg px-2 py-1.5">&quot;{t.body}&quot;</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </TabsContent>

              {/* Receipts */}
              <TabsContent value="receipts" className="mt-4">
                {receipts.length === 0 ? <p className="text-xs text-slate-400 italic">No paid receipts yet.</p> : (
                  <ul className="divide-y divide-slate-100">
                    {receipts.map((r) => {
                      const expanded = expandedHistorySid === r.session_id;
                      const history = historyBySid[r.session_id];
                      return (
                        <li key={r.session_id} className="py-2 text-sm" data-testid={`receipt-row-${r.receipt_id}`}>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{r.medication_name}</p>
                              <p className="text-xs text-slate-400">{r.tier_label} · Receipt {r.receipt_id} · {fmtDate(r.paid_at)}</p>
                              {r.forward_count > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleHistory(r.session_id)}
                                  className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-purple-700 hover:text-purple-900 hover:underline transition-colors"
                                  data-testid={`receipt-forward-meta-${r.receipt_id}`}
                                >
                                  <span className="font-medium">Re-sent {r.forward_count} {r.forward_count === 1 ? "time" : "times"}</span>
                                  {r.last_forward_to && <> · last to <span className="font-mono">{r.last_forward_to}</span></>}
                                  {r.last_forward_at && <> ({fmtDate(r.last_forward_at)})</>}
                                  {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                </button>
                              )}
                            </div>
                            <span className="text-sm font-bold text-slate-900 shrink-0">{fmtMoney(r.amount, r.currency)}</span>
                          </div>
                          {expanded && (
                            <div className="mt-2 ml-0 sm:ml-2 bg-purple-50/60 border border-purple-100 rounded-lg p-3" data-testid={`receipt-history-${r.receipt_id}`}>
                              {loadingHistorySid === r.session_id ? (
                                <p className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading send history…</p>
                              ) : !history || history.length === 0 ? (
                                <p className="text-xs text-slate-500 italic">No send history found.</p>
                              ) : (
                                <table className="w-full text-[11px]">
                                  <thead>
                                    <tr className="text-slate-500 text-left">
                                      <th className="pb-1.5 pr-2 font-medium">When</th>
                                      <th className="pb-1.5 pr-2 font-medium">To</th>
                                      <th className="pb-1.5 pr-2 font-medium">Type</th>
                                      <th className="pb-1.5 pr-2 font-medium">Initiated by</th>
                                      <th className="pb-1.5 font-medium">IP</th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-slate-700">
                                    {history.map((h) => (
                                      <tr key={h.id} className="border-t border-purple-100/60">
                                        <td className="py-1.5 pr-2 whitespace-nowrap">{fmtDate(h.timestamp)}</td>
                                        <td className="py-1.5 pr-2 font-mono break-all">{h.recipient || "—"}</td>
                                        <td className="py-1.5 pr-2">
                                          <span className={`inline-block text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 ${h.action === "receipt_email_forwarded" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                            {h.action === "receipt_email_forwarded" ? "Forward" : "Self"}
                                          </span>
                                        </td>
                                        <td className="py-1.5 pr-2">
                                          {h.sender_name || h.sender_id || "—"}
                                          {h.sender_role && <span className="ml-1 text-slate-400">({h.sender_role})</span>}
                                        </td>
                                        <td className="py-1.5 font-mono text-slate-500">{h.ip_address || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="billing" className="mt-4">
                <PatientPaymentTimeline patientId={patientId} />
              </TabsContent>

              <TabsContent value="rewards" className="mt-4">
                <PatientRewardsTab patientId={patientId} />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


/**
 * Mirrors the cycle-grouping + Strength/Tier pills used in the prescriber
 * dashboard's Patient Record. Reads parent_order_id / dosage_requested /
 * payment_type / series_position from each order and nests linked refill
 * cycles under their cycle-1 parent so investor demos and staff see one
 * cohesive subscription rather than visually duplicated rows.
 */
const PDM_TIER_LABEL = {
  one_time: "Monthly",
  subscription: "Bundle Subscribe",
  solo_subscribe: "Solo Subscribe",
  royal_90: "Royal 90",
  pre_screened: "Pre-screened",
};

function OrdersGroupedList({ orders, fmtDate }) {
  const [expandedId, setExpandedId] = useState(null);
  const byId = new Map(orders.map((o) => [o.id, o]));
  const children = new Map();
  const tops = [];
  for (const o of orders) {
    if (o.parent_order_id && byId.has(o.parent_order_id)) {
      const arr = children.get(o.parent_order_id) || [];
      arr.push(o);
      children.set(o.parent_order_id, arr);
    } else {
      tops.push(o);
    }
  }

  const renderRow = (o, isChild = false) => {
    const isOpen = expandedId === o.id;
    const tierLabel = PDM_TIER_LABEL[o.payment_type] || o.payment_type || null;
    const strength = o.dosage_requested || o.medication_strength || null;
    const kids = children.get(o.id) || [];
    const hasCycles = kids.length > 0;
    return (
      <div
        key={o.id}
        className={`border rounded-lg ${
          isChild
            ? "border-slate-200 dark:border-slate-700 ml-6 bg-slate-50 dark:bg-slate-800/30"
            : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40"
        }`}
        data-testid={`pdm-order-${o.id}`}
      >
        <button
          type="button"
          onClick={() => setExpandedId(isOpen ? null : o.id)}
          className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-medium text-sm truncate">{o.medication_name || "Medication"}</p>
                {strength && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600"
                    data-testid={`pdm-order-strength-${o.id}`}
                  >
                    {strength}
                  </span>
                )}
                {tierLabel && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200 border border-purple-300 dark:border-purple-700/50"
                    data-testid={`pdm-order-tier-${o.id}`}
                  >
                    {tierLabel}
                  </span>
                )}
                {hasCycles && (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700/50"
                    data-testid={`pdm-order-subscription-${o.id}`}
                    title={`${kids.length} linked refill cycle(s) — click to expand`}
                  >
                    Active subscription · {kids.length + 1} cycles
                  </span>
                )}
                {o.series_position && o.total_in_series && o.total_in_series > 1 && (
                  <span className="text-[10px] font-mono text-slate-500">
                    Cycle {o.series_position}/{o.total_in_series}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{o.status} · {o.payment_status} · {fmtDate(o.created_at)}</p>
            </div>
          </div>
          <span className="text-xs font-mono text-slate-400 shrink-0">{o.id?.slice(-8)}</span>
        </button>
        {isOpen && hasCycles && (
          <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-2">
            <p className="text-xs text-slate-500">Linked refill cycles ({kids.length})</p>
            {kids.map((c) => renderRow(c, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="pdm-orders-grouped-list">
      {tops.map((o) => renderRow(o))}
    </div>
  );
}

/**
 * Soft-flag badge for duplicate-address detection. Only shown when the
 * patient profile carries `duplicate_address_flag: true`. Lists the
 * sibling accounts that match this household address and lets an admin
 * clear the flag (e.g. when the matches are legitimately related —
 * spouses, parent + adult child).
 */
function DuplicateAddressBadge({ profile, patientId, onCleared }) {
  const matches = Array.isArray(profile?.duplicate_address_matches) ? profile.duplicate_address_matches : [];
  const [clearing, setClearing] = useState(false);
  const clearFlag = async () => {
    if (!(await brandedConfirm({ title: 'Are you sure?', description: "Clear the duplicate-address soft flag for this patient? Auto-applied discounts (FL, referral, Switch & Save) will be re-enabled.", confirmText: 'Confirm', cancelText: 'Cancel', tone: 'info' }))) return;
    setClearing(true);
    try {
      await API.post(`/admin/users/${patientId}/duplicate-address/clear`);
      toast.success("Duplicate-address flag cleared");
      onCleared?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear flag");
    } finally {
      setClearing(false);
    }
  };
  return (
    <div
      className="mt-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700/60 p-3"
      data-testid="duplicate-address-badge"
    >
      <div className="flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-600 dark:text-amber-300 mt-0.5 shrink-0" />
        <div className="flex-1 text-xs text-amber-900 dark:text-amber-100">
          <p className="font-semibold mb-1">Address matches another account · auto-coupons suppressed</p>
          {matches.length > 0 && (
            <ul className="space-y-0.5 mb-2">
              {matches.slice(0, 5).map((m) => (
                <li key={m.user_id} className="opacity-90">
                  • {m.name || "(no name)"} <span className="opacity-70">— {m.email || "(no email)"}</span>
                </li>
              ))}
              {matches.length > 5 && <li className="opacity-70">+ {matches.length - 5} more…</li>}
            </ul>
          )}
          <p className="opacity-80">
            FL state, referral, and Switch & Save discounts are paused for this checkout to prevent
            household-level discount stacking. Streak / loyalty rewards remain active.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={clearFlag}
          disabled={clearing}
          className="border-amber-500 text-amber-800 hover:bg-amber-100 dark:text-amber-200"
          data-testid="duplicate-address-clear-btn"
        >
          {clearing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Clear flag"}
        </Button>
      </div>
    </div>
  );
}
